import { track } from '@tldraw/tldraw'
import { observer } from 'mobx-react-lite'
import Styles from './ColorsPanel.module.scss'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { Pressable } from '@react-aria/interactions'
import { Color, GradientColor, GradientStop, SolidColor } from 'components/VisualStudioPlayground/VisualEditor/Types'
import { SolidColorsPanelBody } from './SolidColorsPanelBody'
import { SafeNumberInput } from 'almanac-ui/TextInputField/SafeNumberInput'
import { DeleteGradientStopButton } from './DeleteGradientStopButton'
import cx from 'classnames'

interface Props {
  selectedColor: GradientColor
  stop: GradientStop
  minPosition: number
  maxPosition: number
  documentSolidColors: SolidColor[]
  onNewColorChange: (color: Color) => void
}

export const GradientStopEditor = track(
  observer(({ selectedColor, stop, minPosition, maxPosition, documentSolidColors, onNewColorChange }: Props) => {
    const handleSelectStopColor = (solidColor: SolidColor, stop: GradientStop) => {
      const newColor = {
        ...selectedColor,
        stops: selectedColor.stops.map((existingStop) => {
          if (existingStop !== stop) {
            return existingStop
          }

          return {
            ...existingStop,
            color: solidColor.color,
            opacity: solidColor.opacity,
          }
        }),
      }

      onNewColorChange(newColor)
    }

    const handlePositionInput = (stop: GradientStop, updatedPosition: number) => {
      onNewColorChange({
        ...selectedColor,
        stops: selectedColor.stops.map((existingStop) => {
          if (existingStop !== stop) {
            return existingStop
          }

          return {
            ...existingStop,
            position: updatedPosition,
          }
        }),
      })
    }

    return (
      <div className={Styles.gradientStopEditor}>
        <div className={Styles.gradientFieldContainer}>
          <div className={Styles.gradientLabel}>Color</div>
          <DropdownTrigger>
            <Pressable aria-label={`Change stop color at position ${stop.position * 100}%`}>
              <div className={cx(Styles.gradientStopPreviewContainer, { [Styles.chequered]: stop.opacity < 1 })}>
                <div className={Styles.gradientStopPreview} style={{ background: stop.color, opacity: stop.opacity }} />
              </div>
            </Pressable>
            <div className={Styles.stopColorPickerContainer}>
              <SolidColorsPanelBody
                documentSolidColors={documentSolidColors}
                onSelect={(color) => handleSelectStopColor(color as SolidColor, stop)}
                onNewColorChange={(color) => handleSelectStopColor(color as SolidColor, stop)}
                selectedColor={{ type: 'solidColor', color: stop.color, opacity: stop.opacity }}
              />
            </div>
          </DropdownTrigger>
        </div>
        <div className={Styles.gradientFieldContainer}>
          <div className={Styles.gradientLabel}>Position</div>
          <div className={Styles.stopPositionFieldContainer}>
            <SafeNumberInput
              step={1}
              size="sm"
              name={`Stop ${selectedColor.stops.indexOf(stop)} position`}
              minValue={minPosition}
              maxValue={maxPosition}
              value={Math.round(stop.position * 100)}
              onChange={(value) => handlePositionInput(stop, value / 100)}
            />
            <span>%</span>
          </div>
        </div>
        <DeleteGradientStopButton
          selectedColor={selectedColor}
          stopToDelete={stop}
          onNewColorChange={onNewColorChange}
        />
      </div>
    )
  }),
)
